import { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";

import { hashes } from "constants/agent-flags";
import { AgentActions, IAgentFlags, selectLoggedInAgent } from "store/entities/agents";

type Setter<K extends keyof IAgentFlags> = (value: NonNullable<IAgentFlags[K]>) => void;

export function useAgentFlag(): [undefined, () => void];
export function useAgentFlag<K extends keyof typeof hashes>(flag: K): [IAgentFlags[K] | undefined, Setter<K>];
export function useAgentFlag<K extends keyof IAgentFlags>(flag: K): [IAgentFlags[K] | undefined, () => void];
export function useAgentFlag<K extends keyof IAgentFlags>(
  flag?: K
): [undefined, () => void] | [IAgentFlags[K] | undefined, Setter<K>] | [IAgentFlags[K] | undefined, () => void];

export function useAgentFlag<K extends keyof IAgentFlags>(
  flag?: K
):
  | [undefined, () => void]
  | [IAgentFlags[K] | undefined, (value: NonNullable<IAgentFlags[K]>) => void]
  | [IAgentFlags[K] | undefined, () => void] {
  const dispatch = useDispatch();
  const currentAgent = useSelector(selectLoggedInAgent);

  if (flag === undefined) {
    // FIXME: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    return [undefined, () => {}];
  }

  if (!(flag in hashes)) {
    // FIXME: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    return [currentAgent?.flags[flag], () => {}];
  }

  const hash = hashes[flag as keyof typeof hashes];
  const currentValue = currentAgent?.flags[flag];
  const setFlag = useCallback(
    (value: NonNullable<IAgentFlags[K]>) => {
      if (value === currentValue) {
        return;
      }
      dispatch(AgentActions.setFlag({ name: `${flag}_${hash}`, value }));
    },
    [dispatch, flag, hash]
  );

  return [currentValue, setFlag];
}
